import React from 'react';

import merge from 'lodash/merge';
import { getGlobalAppVar } from '../global-app';

const DEFAULT_ASSET_VERSION = 42;
const defaultPreferences = {
  debug: false,
  assetVersion: DEFAULT_ASSET_VERSION,
};

export type Config = {
  cdnImage: boolean;
  contactMail: string;
  google: {
    appId: string;
    maps: {
      apiKey: string;
    };
  };
  facebook: {
    appId: number;
  };
  apple: {
    appId: string;
  };
  algolia: {
    appId: string;
    learningContentApiKey: string;
    anonymizedApprenticesApiKey: string;
    publicJobPostingsApiKey: string;
  };
  opentok: {
    apiKey: number;
  };
  mapbox: {
    account: string;
    careerSurvey: string;
    studentApplication: string;
  };
  openclassrooms: {
    api: {
      anonymous: {
        client_id: string;
        client_secret: string;
      };
      logged: {
        client_id: string;
      };
      urls: {
        authorize: string;
        root: string;
        proxy: string;
      };
    };
  };
};

export const getPreferences = () =>
  merge({}, defaultPreferences, getGlobalAppVar().preferences) as Record<string, any>;

/**
 * @param {string} key
 * @returns *
 */
export const getPreference = (key: string | keyof Config) => getPreferences()[key];

/**
 * @param {string} key
 * @returns boolean
 */
export const isActivated = (key: string | keyof Config) => !!getPreference(key);

export default {
  getPreferences,
  getPreference,
  isActivated,
};

interface PerRequestCache {
  current: ConfigContextType | null;
}

let cache: (() => PerRequestCache) | null = null;

const getCache = () => {
  if (!cache) {
    cache = React.cache((): PerRequestCache => ({ current: null }));
  }
  return cache();
};

export interface ConfigContextType {
  getConfig: <K extends keyof Config>(key: K) => Config[K];
  config: Config | null;
}

export function getConfigContext() {
  return getCache().current;
}

export function setConfigContext(context: ConfigContextType) {
  getCache().current = context;
}

export function createConfigContext(config: Config) {
  const context: ConfigContextType = {
    config,
    getConfig: <K extends keyof Config>(key: K) => config[key],
  };

  return context;
}
